// The line-items table and totals panel, shared by the invoice and quotation
// editors. Both documents price identically, so the arithmetic and the editing
// affordances live here rather than being written twice.

import { useMemo } from 'react';
import { Icon } from './Icon';
import { EmptyState } from './ui';
import { useT } from '../lib/i18n';
import { useSession } from '../lib/session';
import { formatMoney, lineTotal, roundMoney } from '@shared/format';

/** A line being edited. `key` is a client-side identity, not a database id. */
export interface DraftLine {
  key: string;
  productId: number | null;
  code: string;
  description: string;
  quantity: number;
  unitPrice: number;
}

let counter = 0;
export const newLineKey = () => `line-${++counter}`;

/** Mirrors the server's arithmetic exactly, so the preview never disagrees. */
export function useDocumentTotals(lines: DraftLine[], taxRate: number) {
  return useMemo(() => {
    const subtotal = roundMoney(lines.reduce((sum, l) => sum + lineTotal(l.quantity, l.unitPrice), 0));
    const tax = roundMoney((subtotal * taxRate) / 100);
    return { subtotal, tax, total: roundMoney(subtotal + tax) };
  }, [lines, taxRate]);
}

export function DocumentLines({ lines, currencySymbol, readOnly, onChange, onRemove, emptyAction }: {
  lines: DraftLine[];
  currencySymbol: string;
  readOnly?: boolean;
  onChange: (key: string, patch: Partial<DraftLine>) => void;
  onRemove: (key: string) => void;
  emptyAction?: React.ReactNode;
}) {
  const t = useT();
  const { money } = useSession();
  const currency = (value: number) => formatMoney(value, { ...money, symbol: currencySymbol });

  if (lines.length === 0) {
    return (
      <EmptyState
        icon="box"
        title={t('invoice.lines.empty')}
        hint={t('invoice.lines.emptyHint')}
        action={emptyAction}
      />
    );
  }

  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th className="shrink">{t('invoice.lines.code')}</th>
            <th>{t('invoice.lines.description')}</th>
            <th className="shrink num">{t('invoice.lines.quantity')}</th>
            <th className="shrink num">{t('invoice.lines.unitPrice')}</th>
            <th className="num">{t('invoice.lines.lineTotal')}</th>
            {!readOnly && <th className="shrink" />}
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => (
            <tr key={line.key}>
              <td className="shrink mono">{line.code || '—'}</td>
              <td>
                {readOnly ? line.description : (
                  <input
                    className="input"
                    value={line.description}
                    aria-label={t('invoice.lines.description')}
                    onChange={(e) => onChange(line.key, { description: e.target.value })}
                  />
                )}
              </td>
              <td className="shrink num">
                {readOnly ? line.quantity : (
                  <input
                    className="input input--numeric line-qty"
                    inputMode="decimal"
                    aria-label={t('invoice.lines.quantity')}
                    value={line.quantity}
                    // A comma is what a Spanish keyboard produces for decimals.
                    onChange={(e) => onChange(line.key,
                      { quantity: Number(e.target.value.replace(',', '.')) || 0 })}
                  />
                )}
              </td>
              <td className="shrink num">
                {readOnly ? currency(line.unitPrice) : (
                  <input
                    className="input input--numeric line-price"
                    inputMode="decimal"
                    aria-label={t('invoice.lines.unitPrice')}
                    value={line.unitPrice}
                    onChange={(e) => onChange(line.key,
                      { unitPrice: Number(e.target.value.replace(',', '.')) || 0 })}
                  />
                )}
              </td>
              <td className="num table__primary">{currency(lineTotal(line.quantity, line.unitPrice))}</td>
              {!readOnly && (
                <td className="shrink">
                  <button
                    type="button"
                    className="btn btn--ghost btn--icon"
                    onClick={() => onRemove(line.key)}
                    title={t('invoice.lines.remove')}
                    aria-label={t('invoice.lines.remove')}
                  >
                    <Icon name="trash" />
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function TotalsPanel({ subtotal, tax, total, taxRate, currencySymbol, note }: {
  subtotal: number;
  tax: number;
  total: number;
  taxRate: number;
  currencySymbol: string;
  note?: string;
}) {
  const t = useT();
  const { money } = useSession();
  const currency = (value: number) => formatMoney(value, { ...money, symbol: currencySymbol });

  return (
    <div className="card">
      <div className="card__head"><h2 className="card__title">{t('invoice.total')}</h2></div>
      <div className="card__body">
        <div className="totals">
          <div className="totals__row">
            <span className="totals__label">{t('invoice.subtotal')}</span>
            <span className="totals__value">{currency(subtotal)}</span>
          </div>
          <div className="totals__row">
            <span className="totals__label">{t('invoice.tax', { rate: taxRate })}</span>
            <span className="totals__value">{currency(tax)}</span>
          </div>
          <div className="totals__row totals__row--grand">
            <span className="totals__label">{t('invoice.total')}</span>
            <span className="totals__value">{currency(total)}</span>
          </div>
        </div>
        {note && <p className="field__hint" style={{ marginTop: 12 }}>{note}</p>}
      </div>
    </div>
  );
}

/** Merges a picked product into the draft, bumping quantity on a repeat. */
export function addLineTo(lines: DraftLine[], line: Omit<DraftLine, 'key'>): DraftLine[] {
  const index = lines.findIndex(
    (l) => l.productId === line.productId && l.unitPrice === line.unitPrice);
  const existing = index >= 0 ? lines[index] : undefined;
  if (existing) {
    const next = [...lines];
    next[index] = { ...existing, quantity: existing.quantity + line.quantity };
    return next;
  }
  return [...lines, { ...line, key: newLineKey() }];
}
